














import {
  Vue, Component, Prop, PropSync, Ref, VModel, Watch,
} from 'vue-property-decorator';
import type { Audio, Second } from '@/data';
import type IPlayer from './IPlayer';

@Component
export default class FilePlayer extends Vue implements IPlayer {
  @Prop({ required: true })
  audio!: Audio;

  @Ref('track')
  track?: HTMLAudioElement;

  // Timestamp

  // This sync props are only used for output ('update:time)
  // The only way to set it is with setTime, or setting it to null
  // Constantly changing values such as scroll or timestamps should only
  // be changed in one direction using reactivity.
  // For two-way binding, one direction must use explicitely called functions
  @PropSync('currentTime', { default: null })
  currentTimeSync!: Second | null;

  emitEnded() {
    this.$emit('ended');

    this.emitPlaying();

    if (localStorage.getItem('continuePlaying') === 'true') this.$emit('go-to-next');
  }

  // Send state of <audio> to parent
  emitPlaying() {
    const realIsPlaying = this.isTrackPlaying();

    if (this.playing !== realIsPlaying) this.playing = realIsPlaying;
  }

  // Send progress of <audio> to parent
  onTimeUpdate() {
    if (!this.track || !this.isTrackPlaying()) return;

    if (this.track.currentTime !== this.currentTimeSync) {
      this.currentTimeSync = this.track.currentTime as Second;
    }

    this.emitPlaying();
  }

  // Start the animation, to send faster time events
  mounted() {
    this.startAnimation(0 as Second);
  }

  // Add additional update:time events, faster than the dom provides
  startAnimation(lastTime: Second) {
    requestAnimationFrame(() => {
      if (!this.track) return;

      const newTime = this.track.currentTime as Second;
      // Only send update if time has changed and not currently seeking
      if (lastTime !== newTime) {
        this.currentTimeSync = newTime;
      }

      this.startAnimation(newTime);
    });
  }

  public setCurrentTime(second: Second) {
    if (!this.track) return;

    this.track.currentTime = second;

    // If track is paused, manually send update that currentTime has changed
    if (!this.isTrackPlaying()) {
      this.currentTimeSync = second;
    }
  }

  public reset() {
    this.playing = false;
    this.currentTimeSync = null;

    if (!this.track) return;

    this.track.pause();
    this.track.currentTime = 0;
  }

  @Watch('playing')
  ripple() {
    const playButton = document.getElementById('play-button');
    if (playButton) {
      const offset = playButton.getBoundingClientRect();

      const ev = new MouseEvent('mousedown', {
        clientX: offset.left + offset.width / 2,
        clientY: offset.top + offset.height / 2,
      });
      playButton.dispatchEvent(ev);

      setTimeout(() => {
        playButton.dispatchEvent(new MouseEvent('mouseup'));
      }, 1);
    }
  }

  // Play state

  @VModel({ default: false })
  playing!: boolean;

  // https://stackoverflow.com/a/31196707/2657597
  isTrackPlaying(): boolean {
    if (!this.track) return false;

    return !!(
      this.track.played
      && !this.track.paused
      && !this.track.ended
      && this.track.readyState > 2
    );
  }

  // Upward updates
  @Watch('audio')
  async onAudioChange() {
    await Vue.nextTick();
    this.onPlayChanged();
  }

  @Watch('value')
  onPlayChanged() {
    if (!this.track) return;

    if (!this.isTrackPlaying() && this.playing) this.track.play();
    else if (this.isTrackPlaying() && !this.playing) this.track.pause();
  }

  emitError(e: Error) {
    this.$emit('error', e);
  }
}
